/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in continuity-diagram.css. */
import { useEffect, useId, useRef, useState } from "react"

import { useElementWidth } from "@/hooks/useElementWidth"
import { useIsVisible } from "@/hooks/useIsVisible"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { cardRingClassName } from "@/routes/-components/home-new/homeNewStyles"

import {
  BOX_TIPS,
  CHIP_HOLD,
  CHIP_IDS,
  CURRENT,
  DESKTOP_LAYOUT,
  GOSSIP_TIP,
  MOBILE_LAYOUT,
  PHASES,
  PILL_DELAY,
  PILL_LABELS,
  PILL_TIPS,
  RESTLESS,
  STALE,
  STATUS_IDS,
  WIRE_IDS,
  type ChipId,
  type Layout,
  type PillId,
  type StatusId,
  type Tip,
} from "./continuity-diagram-data"
import { DiagramTooltip } from "./diagram-tooltip"

import "./continuity-diagram.css"

// ---------------------------------------------------------------------------
// Timeline. One clock (ms) drives everything; each chip and status reading is a
// run of consecutive steps sharing a value, so a reading that holds across
// steps keeps animating from where it started instead of restarting.
// ---------------------------------------------------------------------------

const PHASE_STARTS = PHASES.reduce<number[]>((starts, _, index) => {
  starts.push(index === 0 ? 0 : starts[index - 1]! + PHASES[index - 1]!.duration)
  return starts
}, [])
const LOOP_TOTAL = PHASE_STARTS[PHASES.length - 1]! + PHASES[PHASES.length - 1]!.duration

/**
 * How far into each step its delayed chips and pills have all landed: a step
 * picked while paused shows this frame, not the step's empty first instant.
 */
const SETTLE_AT = PHASES.map((_, index) =>
  Math.max(
    0,
    ...Object.values(CHIP_HOLD).map((hold) => hold[index] ?? 0),
    ...Object.values(PILL_DELAY).map((delay) => delay[index] ?? 0),
  ),
)

function phaseAt(time: number) {
  const index = PHASE_STARTS.findIndex((start, i) => time < start + PHASES[i]!.duration)
  return index === -1 ? PHASES.length - 1 : index
}

interface Segment {
  key: string | null
  prevKey: string | null
  start: number
  end: number
  holdBefore: number
}

function buildSegments(getValue: (phase: number) => string | null, hold?: Record<number, number>) {
  const segments: Segment[] = []
  PHASES.forEach((phase, index) => {
    const key = getValue(index)
    const start = PHASE_STARTS[index]!
    const end = start + phase.duration
    const last = segments[segments.length - 1]
    if (last && last.key === key) {
      last.end = end
    } else {
      segments.push({
        key,
        prevKey: last ? last.key : null,
        start,
        end,
        holdBefore: hold?.[index] ?? 0,
      })
    }
  })
  return segments
}

function segmentAt(segments: Segment[], time: number) {
  return segments.find((s) => time >= s.start && time < s.end) ?? segments[segments.length - 1]!
}

const CHIP_SEGMENTS = Object.fromEntries(
  CHIP_IDS.map((id) => [id, buildSegments((p) => PHASES[p]!.chips[id], CHIP_HOLD[id])]),
) as Record<ChipId, Segment[]>

const STATUS_SEGMENTS = Object.fromEntries(
  STATUS_IDS.map((id) => [id, buildSegments((p) => PHASES[p]!.status[id])]),
) as Record<StatusId, Segment[]>

// Three text animations, picked by what was in the slot before: type out when
// it was empty, split-flap when it read something else, and a periodic re-flip
// plus a soft breath for readings that are still working (RESTLESS).
const MS_PER_CHAR = 78
const FLIP_STAGGER = 45
const FLIP_SPIN = 330
const FLIP_TICK = 55
const FLIP_GLYPHS = "abcdefghijklmnopqrstuvwxyz0123456789"
const REST_HOLD = 900
const REST_CYCLE = 4200

interface TextFrame {
  target: string
  text: string
  glow: number
}

function typeOut(text: string, elapsed: number) {
  const typed = Math.floor(elapsed / MS_PER_CHAR)
  return typed >= text.length ? text : `${text.slice(0, Math.max(0, typed))}█`
}

function flipSpan(length: number) {
  return (length - 1) * FLIP_STAGGER + FLIP_SPIN
}

function splitFlap(text: string, previous: string, elapsed: number) {
  // Right-align the old reading to the new one's columns so it doesn't drift.
  const prev =
    previous.length < text.length
      ? previous.padStart(text.length, " ")
      : previous.slice(previous.length - text.length)
  let out = ""
  for (let i = 0; i < text.length; i++) {
    const start = i * FLIP_STAGGER
    if (elapsed < start) {
      out += prev.charAt(i) || " "
    } else if (elapsed < start + FLIP_SPIN) {
      const tick = Math.floor((elapsed - start + i * 7) / FLIP_TICK)
      out += FLIP_GLYPHS.charAt(tick % FLIP_GLYPHS.length)
    } else {
      out += text.charAt(i)
    }
  }
  return out
}

function breath(sinceLead: number) {
  const progress = (sinceLead % REST_CYCLE) / REST_CYCLE
  return 0.7 + 0.3 * Math.cos(2 * Math.PI * progress)
}

function textFrame(text: string, previous: string | null, elapsed: number, hold: number) {
  let lead = 0
  if (previous === null) {
    lead = text.length * MS_PER_CHAR
    if (elapsed < lead) return { text: typeOut(text, elapsed), glow: 1 }
  } else if (previous !== text) {
    lead = flipSpan(text.length)
    if (elapsed < lead) return { text: splitFlap(text, previous, elapsed), glow: 1 }
  }
  if (!RESTLESS.has(text)) return { text, glow: 1 }
  const waitFor = lead + REST_HOLD
  if (elapsed < waitFor) return { text, glow: 1 }
  const span = flipSpan(text.length)
  const sinceLead = elapsed - waitFor
  const passStart = waitFor + Math.floor(sinceLead / REST_CYCLE) * REST_CYCLE
  // Never start a re-flip the step can't finish.
  if (passStart + span > hold) return { text, glow: 1 }
  if (elapsed < passStart + span) {
    return { text: splitFlap(text, text, elapsed - passStart), glow: breath(sinceLead) }
  }
  return { text, glow: breath(sinceLead) }
}

interface SceneFrame {
  phase: number
  progress: number
  wires: Set<string>
  pills: Set<PillId>
  chips: Record<ChipId, TextFrame>
  status: Record<StatusId, TextFrame | null>
}

function sceneAt(time: number, live: boolean): SceneFrame {
  const phase = phaseAt(time)
  const data = PHASES[phase]!
  const phaseElapsed = time - PHASE_STARTS[phase]!

  const pills = new Set<PillId>(
    data.pills.filter((id) => phaseElapsed >= (PILL_DELAY[id]?.[phase] ?? 0)),
  )

  const chips = {} as Record<ChipId, TextFrame>
  for (const id of CHIP_IDS) {
    const seg = segmentAt(CHIP_SEGMENTS[id], time)
    const key = seg.key ?? ""
    const elapsed = time - seg.start
    if (seg.holdBefore && elapsed < seg.holdBefore && seg.prevKey !== null) {
      chips[id] = { target: seg.prevKey, text: seg.prevKey, glow: 1 }
      continue
    }
    const frame = live
      ? textFrame(key, seg.prevKey, elapsed - seg.holdBefore, seg.end - seg.start - seg.holdBefore)
      : { text: key, glow: 1 }
    chips[id] = { target: key, ...frame }
  }

  const status = {} as Record<StatusId, TextFrame | null>
  for (const id of STATUS_IDS) {
    const seg = segmentAt(STATUS_SEGMENTS[id], time)
    if (seg.key === null) {
      status[id] = null
      continue
    }
    const frame = live
      ? textFrame(seg.key, seg.prevKey, time - seg.start, seg.end - seg.start)
      : { text: seg.key, glow: 1 }
    status[id] = { target: seg.key, ...frame }
  }

  return {
    phase,
    progress: phaseElapsed / data.duration,
    wires: new Set(data.wires),
    pills,
    chips,
    status,
  }
}

// ---------------------------------------------------------------------------
// Drawing. Text is Entire Mono (0.6em advance), so a slot's position comes
// from the settled reading's length and a half-typed or mid-flip string never
// slides around while it resolves.
// ---------------------------------------------------------------------------

const MONO_ADVANCE = 0.6
const CHIP_SIZE = 25
const STATUS_SIZE = 20
const LABEL_SIZE = 22
const BOX_HEADER = 80
const BOX_HEIGHT = 191
const CLIENT_HEIGHT = 64

function roundedPath(points: [number, number][], radius = 12) {
  const [first, ...rest] = points
  if (!first) return ""
  let d = `M${first[0]} ${first[1]}`
  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i - 1]!
    const [cx, cy] = points[i]!
    const [nx, ny] = points[i + 1]!
    const inLength = Math.hypot(cx - px, cy - py)
    const outLength = Math.hypot(nx - cx, ny - cy)
    const r = Math.min(radius, inLength / 2, outLength / 2)
    const ax = cx - ((cx - px) / inLength) * r
    const ay = cy - ((cy - py) / inLength) * r
    const bx = cx + ((nx - cx) / outLength) * r
    const by = cy + ((ny - cy) / outLength) * r
    d += ` L${ax} ${ay} Q${cx} ${cy} ${bx} ${by}`
  }
  const last = rest[rest.length - 1] ?? first
  return `${d} L${last[0]} ${last[1]}`
}

function chipTone(value: string) {
  return value === STALE ? "stale" : "current"
}

/** A hash in its tinted box, centred on (cx, cy). */
function Chip({ cx, cy, frame }: { cx: number; cy: number; frame: TextFrame }) {
  const tone = chipTone(frame.target)
  return (
    <g>
      <rect
        className={`continuity-chip-bg continuity-chip-bg--${tone}`}
        x={cx - 36.5}
        y={cy - 20.5}
        width={73}
        height={41}
        rx={12}
      />
      <text
        className={`continuity-chip-text--${tone}`}
        x={cx - (frame.target.length * CHIP_SIZE * MONO_ADVANCE) / 2}
        y={cy}
        fontSize={CHIP_SIZE}
        opacity={frame.glow}
      >
        {frame.text}
      </text>
    </g>
  )
}

/** A status reading right-aligned at endX; a trailing hash gets the chip's box once it lands. */
function Status({ endX, cy, frame }: { endX: number; cy: number; frame: TextFrame }) {
  const advance = STATUS_SIZE * MONO_ADVANCE
  const startX = endX - frame.target.length * advance
  const hasHash = frame.target.endsWith(` ${CURRENT}`)
  const settled = frame.text === frame.target
  const boxHeight = (STATUS_SIZE * 41) / 25
  return (
    <g>
      {hasHash ? (
        <rect
          className="continuity-chip-bg continuity-chip-bg--current"
          x={endX - CURRENT.length * advance - 7}
          y={cy - boxHeight / 2}
          width={CURRENT.length * advance + 14}
          height={boxHeight}
          rx={(STATUS_SIZE * 12) / 25}
          opacity={settled ? frame.glow : 0}
        />
      ) : null}
      <text
        className="continuity-status"
        x={startX}
        y={cy}
        fontSize={STATUS_SIZE}
        opacity={frame.glow}
      >
        {frame.text}
      </text>
    </g>
  )
}

function ClientBox({
  x,
  y,
  w,
  label,
  frame,
  tip,
}: {
  x: number
  y: number
  w: number
  label: string
  frame: TextFrame
  tip: Tip
}) {
  const textX = x + 16
  const chipCx = textX + label.length * CHIP_SIZE * MONO_ADVANCE + 12 + 36.5
  const cy = y + CLIENT_HEIGHT / 2
  return (
    <g data-tip-title={tip.title} data-tip={tip.body}>
      <rect className="continuity-header" x={x} y={y} width={w} height={CLIENT_HEIGHT} rx={16} />
      <text className="continuity-ink" x={textX} y={cy} fontSize={CHIP_SIZE}>
        {label}
      </text>
      <Chip cx={chipCx} cy={cy} frame={frame} />
    </g>
  )
}

function NodeBox({
  x,
  y,
  w,
  title,
  lane,
  chip,
  status,
  tip,
}: {
  x: number
  y: number
  w: number
  title: string
  lane?: string
  chip: TextFrame
  status: TextFrame | null
  tip: Tip
}) {
  const rowY = y + BOX_HEADER + 25
  const rowCy = rowY + 32
  return (
    <g data-tip-title={tip.title} data-tip={tip.body}>
      <rect className="continuity-header" x={x} y={y} width={w} height={BOX_HEIGHT} rx={24} />
      <text className="continuity-title" x={x + 25} y={y + BOX_HEADER / 2} fontSize={LABEL_SIZE}>
        {title}
      </text>
      {lane ? (
        <text
          className="continuity-lane"
          x={x + w - 27}
          y={y + BOX_HEADER / 2}
          fontSize={LABEL_SIZE}
          textAnchor="end"
        >
          {lane}
        </text>
      ) : null}
      <rect
        className="continuity-body"
        x={x}
        y={y + BOX_HEADER}
        width={w}
        height={BOX_HEIGHT - BOX_HEADER}
        rx={24}
      />
      <rect className="continuity-row" x={x + 25} y={rowY} width={w - 50} height={64} rx={16} />
      <text className="continuity-ink" x={x + 41} y={rowCy} fontSize={CHIP_SIZE}>
        main
      </text>
      <Chip cx={x + 151.5} cy={rowCy} frame={chip} />
      {status ? <Status endX={x + w - 40} cy={rowCy} frame={status} /> : null}
    </g>
  )
}

function Pill({ id, x, y, on }: { id: PillId; x: number; y: number; on: boolean }) {
  const label = PILL_LABELS[id]
  const lines = Array.isArray(label) ? label : [label]
  const longest = Math.max(...lines.map((line) => line.length))
  const width = longest * LABEL_SIZE * MONO_ADVANCE + 40
  const height = lines.length > 1 ? 70 : 52
  return (
    <g
      className={`continuity-pill ${on ? "is-on" : ""}`}
      data-tip-title={lines.join(" ")}
      data-tip={PILL_TIPS[id]}
    >
      <rect
        className="continuity-canvas"
        x={x - width / 2}
        y={y - height / 2}
        width={width}
        height={height}
        rx={height / 2}
      />
      <text className="continuity-pill-text" x={x} y={y} fontSize={LABEL_SIZE} textAnchor="middle">
        {lines.map((line, index) => (
          <tspan key={line} x={x} dy={index === 0 ? -((lines.length - 1) * 13) : 26}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  )
}

/** A storage-bucket cube for the S3 label. */
function BucketIcon({ x, y }: { x: number; y: number }) {
  return (
    <g className="continuity-icon" transform={`translate(${x} ${y})`}>
      <path d="M12 1.5 22 7v10l-10 5.5L2 17V7z" strokeWidth={2} strokeLinejoin="round" />
      <path d="M2 7l10 5.5L22 7M12 12.5v10" strokeWidth={2} strokeLinejoin="round" />
    </g>
  )
}

function Scene({ layout, frame }: { layout: Layout; frame: SceneFrame }) {
  const markerId = `continuity-arrow-${useId().replace(/:/g, "")}`
  const { s3 } = layout
  return (
    <svg
      className="continuity-svg"
      viewBox={layout.viewBox.join(" ")}
      role="img"
      aria-label="Node A and Node B in front of S3, which holds the WAL object and the WAL index."
    >
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX={9}
          refY={5}
          markerWidth={12}
          markerHeight={12}
          markerUnits="userSpaceOnUse"
          orient="auto"
        >
          <path className="continuity-arrow" d="M0 0.8 9.5 5 0 9.2z" />
        </marker>
      </defs>

      <g data-tip-title={BOX_TIPS.s3.title} data-tip={BOX_TIPS.s3.body}>
        <rect className="continuity-s3" x={s3.x} y={s3.y} width={s3.w} height={s3.h} rx={23} />
        {/* The strip above the WAL boxes, so the whole label row is hoverable. */}
        <rect
          className="continuity-hit"
          x={s3.x}
          y={s3.y}
          width={s3.w}
          height={layout.walObj.y - s3.y}
          rx={23}
        />
        <BucketIcon x={s3.x + 29} y={s3.y + 32} />
        <text
          className="continuity-lane continuity-caps"
          x={s3.x + 66}
          y={s3.y + 44}
          fontSize={LABEL_SIZE}
        >
          S3 · source of truth
        </text>
      </g>

      {WIRE_IDS.map((id) => {
        const d = roundedPath(layout.wires[id])
        return (
          <g key={id} className={`continuity-wire ${frame.wires.has(id) ? "is-on" : ""}`}>
            <path className="continuity-wire-line" d={d} markerEnd={`url(#${markerId})`} />
            <path className="continuity-flow" d={d} />
            {id === "gossip" ? (
              <path
                className="continuity-wire-hit"
                d={d}
                data-tip-title={GOSSIP_TIP.title}
                data-tip={GOSSIP_TIP.body}
              />
            ) : null}
          </g>
        )
      })}

      <ClientBox
        {...layout.push}
        label="push"
        frame={{ target: CURRENT, text: CURRENT, glow: 1 }}
        tip={BOX_TIPS.push}
      />
      <ClientBox
        {...layout.fetch}
        label="fetch main"
        frame={frame.chips.fetchTab}
        tip={BOX_TIPS.fetch}
      />
      <NodeBox
        {...layout.nodeA}
        title="Node A"
        tip={BOX_TIPS.nodeA}
        lane="push lane"
        chip={frame.chips.nodeA}
        status={frame.status.nodeA}
      />
      <NodeBox
        {...layout.nodeB}
        title="Node B"
        tip={BOX_TIPS.nodeB}
        lane="fetch lane"
        chip={frame.chips.nodeB}
        status={frame.status.nodeB}
      />
      <NodeBox
        {...layout.walObj}
        title="WAL object"
        tip={BOX_TIPS.walObj}
        chip={frame.chips.walObj}
        status={frame.status.walObj}
      />
      <NodeBox
        {...layout.walIndex}
        title="WAL index"
        tip={BOX_TIPS.walIndex}
        chip={frame.chips.walIndex}
        status={frame.status.walIndex}
      />

      {(Object.keys(PILL_LABELS) as PillId[]).map((id) => {
        const [x, y] = layout.pills[id]
        return <Pill key={id} id={id} x={x} y={y} on={frame.pills.has(id)} />
      })}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MOBILE_BREAKPOINT = 560

function PlayIcon({ playing }: { playing: boolean }) {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5 fill-current" aria-hidden="true">
      {playing ? (
        <>
          <rect x="4" y="3" width="3" height="10" rx="1" />
          <rect x="9" y="3" width="3" height="10" rx="1" />
        </>
      ) : (
        <path d="M4.5 2.8 13 8l-8.5 5.2z" />
      )}
    </svg>
  )
}

/**
 * Cursor's Continuity design, step by step: a push is durable once its WAL
 * object lands in S3 and visible once Node A moves the WAL index with a CAS;
 * a fetch on another node checks the index (an eTag-conditional GET) before
 * serving from its cache. Plays only while on screen; reduced motion shows the
 * settled final step, and the step bar still jumps between steps.
 */
export function ContinuityDiagram() {
  const rootRef = useRef<HTMLElement>(null)
  const [time, setTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  // The step picked from the step bar while paused, shown settled until playback resumes.
  const [pickedStep, setPickedStep] = useState<number | null>(null)
  const width = useElementWidth(rootRef)
  const isVisible = useIsVisible(rootRef)
  const prefersReducedMotion = usePrefersReducedMotion()

  const live = isPlaying && !prefersReducedMotion
  let displayTime = time
  if (!live && pickedStep !== null) displayTime = PHASE_STARTS[pickedStep]! + SETTLE_AT[pickedStep]!
  else if (prefersReducedMotion) displayTime = LOOP_TOTAL - 1
  const frame = sceneAt(displayTime, live)
  const layout = width < MOBILE_BREAKPOINT ? MOBILE_LAYOUT : DESKTOP_LAYOUT
  const animating = live && isVisible

  useEffect(() => {
    if (!animating) return
    let previous: number | null = null
    let handle = 0
    const tick = (now: number) => {
      const delta = previous === null ? 0 : Math.min(100, now - previous)
      previous = now
      setTime((current) => (current + delta) % LOOP_TOTAL)
      handle = window.requestAnimationFrame(tick)
    }
    handle = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(handle)
  }, [animating])

  function selectStep(index: number) {
    // Playback resumes from the step's beginning.
    setTime(PHASE_STARTS[index]!)
    setIsPlaying(false)
    setPickedStep(index)
  }

  function togglePlaying() {
    setPickedStep(null)
    setIsPlaying((playing) => !playing)
  }

  const step = frame.phase + 1
  const counter = `${String(step).padStart(2, "0")} / ${String(PHASES.length).padStart(2, "0")}`

  return (
    <figure
      ref={rootRef}
      className={`continuity-diagram not-prose my-10 overflow-hidden rounded-xl bg-raised font-sans ${cardRingClassName}`}
      aria-label="Cursor Continuity: how a push becomes durable in S3 and how a fetch on another node stays current"
      data-live={animating ? "true" : "false"}
    >
      <div className="flex h-10 items-center gap-2 px-4 text-xs leading-4">
        <span className="shrink-0 text-muted">Cursor Continuity</span>
        <span className="min-w-0 flex-1 truncate text-right font-semibold text-default">
          The write-ahead log becomes the source of truth
        </span>
      </div>

      <div className="continuity-stage">
        <Scene layout={layout} frame={frame} />
      </div>

      <div className="continuity-segments" role="group" aria-label="Steps">
        {PHASES.map((phase, index) => {
          const fill = index < frame.phase ? 1 : index === frame.phase ? frame.progress : 0
          return (
            <button
              key={phase.caption}
              type="button"
              className="continuity-segment"
              aria-label={`Step ${index + 1}: ${phase.caption}`}
              aria-current={index === frame.phase ? "step" : undefined}
              onClick={() => selectStep(index)}
            >
              <span>
                <span style={{ width: `${fill * 100}%` }} />
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex flex-col gap-2 px-4 pt-3 pb-4">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs leading-4 text-muted tabular-nums">{counter}</span>
          {prefersReducedMotion ? null : (
            <button
              type="button"
              className="continuity-play ml-auto"
              aria-label={isPlaying ? "Pause the animation" : "Play the animation"}
              onClick={togglePlaying}
            >
              <PlayIcon playing={isPlaying} />
            </button>
          )}
        </div>
        <figcaption className="min-h-10 text-sm leading-5 text-muted">
          {PHASES[frame.phase]!.caption}
        </figcaption>
      </div>
      <DiagramTooltip rootRef={rootRef} />
    </figure>
  )
}
